import { useEffect, useMemo, useState } from 'react';
import { buildRgContext } from '@/features/ai-angel/rgKnowledge.mjs';

const categoryLabel: Record<string, string> = {
  peace: '平安／事業／學業',
  relationship: '感情／婚姻／人緣',
  wealth: '財富／正偏財',
  health: '健康',
  general: '綜合事件',
};

const mentorLead: Record<string, string> = {
  C01: '先不用急著說服任何人。我們把反應放慢一點，替你取回選擇的空間。',
  C02: '我們先分開外在期待、情緒和身體訊號，聽一聽較完整的本心。',
  C03: '反覆出現不代表你做錯了；它可能正在提醒你，有一條舊根系需要被看見。',
  C04: '這件事不只是一個點。我陪你退後一步看全局，找出能帶動其他問題的主軸。',
  C05: '懂得很多已經是起點；現在只要讓一點真，經過你成為一個看得見的行動。',
  C06: '你不必再靠硬撐證明自己。臣服是停止耗損，重新選擇真正該承擔的部分。',
  C07: '我們不求一次補完所有缺口，只確認你的角色位置，以及現在能交付的一小步。',
  C08: '先不追問自己夠不夠高。我們從六力中找到最低缺口，讓整體重新站穩。',
  C09: '走到這裡，重點不只是個人成就，而是如何讓所得成為可持續的生命供應。',
  C10: '不必靠一次衝刺完成一年；今天留下一筆真實紀錄，就是可以延續的開始。',
};

export default function RgChapterGuide() {
  const [plan, setPlan] = useState<'chapter' | 'mentor'>('chapter');
  const [question, setQuestion] = useState('');
  const [submittedQuestion, setSubmittedQuestion] = useState('');
  const [journey, setJourney] = useState<{ original: string; path: string[]; current: number; reflections: string[] } | null>(null);
  const [reflection, setReflection] = useState('');
  const result = useMemo(
    () => submittedQuestion ? buildRgContext(submittedQuestion, plan, journey ? {
      mentorPath: journey.path,
      activeChapterId: journey.path[journey.current],
    } : {}) : null,
    [submittedQuestion, plan, journey],
  );

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem('ai-angel-rg-mentor-journey');
      if (!saved) return;
      const restored = JSON.parse(saved);
      if (!restored?.original || !Array.isArray(restored.path)) return;
      setPlan('mentor');
      setQuestion(restored.original);
      setSubmittedQuestion(restored.original);
      setJourney(restored);
    } catch {
      // 無痕模式或舊資料損壞時，仍可正常開始新旅程。
    }
  }, []);

  useEffect(() => {
    if (!journey) return;
    try {
      window.localStorage.setItem('ai-angel-rg-mentor-journey', JSON.stringify(journey));
    } catch {
      // 儲存不可用時不阻擋當次對話。
    }
  }, [journey]);

  const startJourney = (original: string, path: string[]) => {
    setJourney({ original, path, current: 0, reflections: [] });
    setReflection('');
  };

  const submitQuestion = () => {
    const original = question.trim();
    setSubmittedQuestion(original);
    setJourney(null);
    setReflection('');
    try { window.localStorage.removeItem('ai-angel-rg-mentor-journey'); } catch { /* 不阻擋新對話 */ }
    if (plan === 'mentor') {
      const routed = buildRgContext(original, 'mentor');
      if (!routed.needsClarification && routed.chapters.length === 3) {
        startJourney(original, routed.chapters.map(({ id }) => id));
      }
    }
  };

  const continueFromClarification = (routeHint: string, mentorPath?: string[]) => {
    const combined = `${submittedQuestion}\n補充：${routeHint}`;
    setQuestion(combined);
    setSubmittedQuestion(combined);
    if (plan === 'mentor') {
      const path = mentorPath?.length === 3
        ? mentorPath
        : buildRgContext(combined, 'mentor').chapters.map(({ id }) => id);
      if (path.length === 3) startJourney(combined, path);
    }
  };

  const saveReflection = (moveNext: boolean) => {
    if (!journey || !reflection.trim()) return;
    const reflections = [...journey.reflections];
    reflections[journey.current] = reflection.trim();
    const current = moveNext ? Math.min(journey.current + 1, journey.path.length - 1) : journey.current;
    setJourney({ ...journey, current, reflections });
    setReflection(moveNext ? (reflections[current] || '') : '');
  };

  return (
    <section className="bg-background-100 border border-foreground-200/40 rounded-xl p-5 md:p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <p className="text-[11px] tracking-wider uppercase text-primary-600">Final v0.8｜出版前測試</p>
          <h2 className="text-lg font-medium text-foreground-800 mt-1">RG逐章AI Angel</h2>
          <p className="text-xs text-foreground-500 mt-1">只載入必要章卡；AI陪伴、整理與提示，不判定維度、授權或主權積分。</p>
        </div>
        <span className="shrink-0 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1 text-[11px]">ABAA測試模式</span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button type="button" onClick={() => setPlan('chapter')} className={`rounded-lg border px-3 py-3 text-left ${plan === 'chapter' ? 'border-primary-400 bg-primary-50' : 'border-foreground-200 bg-background-50'}`}>
          <span className="block text-sm font-medium">章節方案</span>
          <span className="block text-[11px] text-foreground-500 mt-1">每次只取一章</span>
        </button>
        <button type="button" onClick={() => setPlan('mentor')} className={`rounded-lg border px-3 py-3 text-left ${plan === 'mentor' ? 'border-primary-400 bg-primary-50' : 'border-foreground-200 bg-background-50'}`}>
          <span className="block text-sm font-medium">Mentor方案</span>
          <span className="block text-[11px] text-foreground-500 mt-1">最多跨三章整合</span>
        </button>
      </div>

      <label className="block text-xs text-foreground-600 mb-2" htmlFor="rg-question">請寫下一個正在發生的真實事件</label>
      <textarea id="rg-question" rows={4} value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="例如：我和家人談到金錢時又開始討好，不敢說出界線……" className="w-full rounded-lg border border-foreground-200 bg-background-50 px-3 py-3 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-300" />
      <button type="button" disabled={!question.trim()} onClick={submitQuestion} className="mt-3 w-full rounded-lg bg-primary-500 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40">
        五秒歸零並尋找主章
      </button>

      {result && (
        <div className="mt-6 space-y-4" aria-live="polite">
          <article className="rounded-xl border border-foreground-200 bg-background-50 p-4">
            <p className="text-[11px] text-foreground-400">我聽見你在說</p>
            <p className="text-sm leading-relaxed text-foreground-700 mt-1">「{result.userEcho}」</p>
          </article>
          {result.needsClarification && (
            <article className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
              <p className="text-[11px] font-medium tracking-wider text-amber-700">先澄清再選章</p>
              <p className="text-sm leading-relaxed text-foreground-700 mt-2">我還不想替你猜成C01。{result.clarificationQuestion}</p>
              <div className="grid gap-2 mt-4">
                {result.clarificationOptions.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => continueFromClarification(option.routeHint, option.mentorPath)}
                    className="rounded-lg border border-amber-200 bg-background-50 px-3 py-2.5 text-left text-sm text-foreground-700 hover:border-amber-400 hover:bg-amber-50 transition-colors"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </article>
          )}
          {result.requiresSafetyReferral && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              目前文字含有立即安全風險訊號。請先聯絡當地緊急服務、可信任的人或合格醫療／心理專業；AI Angel不取代緊急與專業處置。
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
            <div className="rounded-lg bg-background-50 border border-foreground-200/50 p-3"><p className="text-[10px] text-foreground-400">入口</p><p className="text-xs mt-1">{categoryLabel[result.primaryCategory]}</p></div>
            <div className="rounded-lg bg-background-50 border border-foreground-200/50 p-3"><p className="text-[10px] text-foreground-400">{journey ? '旅程／本回合載入' : '載入章數'}</p><p className="text-xs mt-1">{journey ? `${result.chapters.length}章／1章` : `${result.chapters.length}章`}</p></div>
            <div className="rounded-lg bg-background-50 border border-foreground-200/50 p-3"><p className="text-[10px] text-foreground-400">本次字數</p><p className="text-xs mt-1">{result.loadedChars.toLocaleString()}</p></div>
            <div className="rounded-lg bg-background-50 border border-foreground-200/50 p-3"><p className="text-[10px] text-foreground-400">相對V0.8節省</p><p className="text-xs mt-1">{result.savedPercent}%</p></div>
          </div>
          {result.mentorIntegration && (
            <article className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
              <p className="text-[11px] font-medium tracking-wider text-amber-700">MENTOR 跨章整合</p>
              <h3 className="font-medium text-foreground-800 mt-1">這個狀態與 {result.chapters.map(({ id }) => id).join(' → ')} 三章有關，我會一步一步陪你走。</h3>
              <p className="text-sm leading-relaxed text-foreground-700 mt-3">先陪你走第 {journey ? journey.current + 1 : 1} 站；不急著證明懂了，只看此刻能否回到本心，做出一個中軸的改變。</p>
              <div className="flex gap-2 mt-4" aria-label="Mentor旅程進度">
                {result.chapters.map((chapter, index) => (
                  <span key={chapter.id} className={`flex-1 rounded-full px-2 py-1 text-center text-[11px] ${journey && index === journey.current ? 'bg-amber-500 text-white' : journey && index < journey.current ? 'bg-amber-100 text-amber-700' : 'bg-background-50 text-foreground-400'}`}>
                    {chapter.id}
                  </span>
                ))}
              </div>
            </article>
          )}
          {!journey && result.chapters.map((chapter, index) => (
            <article key={chapter.id} className="rounded-xl border border-primary-100 bg-primary-50/40 p-4">
              <p className="text-[11px] text-primary-600">{index === 0 ? '本次主章' : 'Mentor關聯章'}｜{chapter.id}</p>
              <h3 className="font-medium text-foreground-800 mt-1">{chapter.title}</h3>
              <p className="text-sm leading-relaxed text-foreground-600 mt-3"><strong>你現在可能卡在：</strong>{chapter.summary}</p>
              <p className="text-sm leading-relaxed text-foreground-700 mt-3"><strong>五秒歸零：</strong>{chapter.prompt}</p>
              <p className="text-sm leading-relaxed text-foreground-600 mt-3"><strong>今天的一個行動：</strong>{chapter.action}</p>
              <p className="text-sm leading-relaxed text-foreground-600 mt-2"><strong>要留下的Evidence：</strong>{chapter.evidence}</p>
            </article>
          ))}
          {journey && result.activeChapter && (
            <div className="space-y-3">
              {journey.reflections.map((answer, index) => {
                if (!answer || index >= journey.current) return null;
                const chapter = result.chapters[index];
                return (
                  <div key={`${chapter.id}-${index}`} className="space-y-2">
                    <article className="mr-8 rounded-2xl rounded-tl-sm border border-primary-100 bg-primary-50/50 p-4">
                      <p className="text-[11px] text-primary-600">AI Angel｜{chapter.id}</p>
                      <p className="text-sm leading-relaxed text-foreground-700 mt-2">{mentorLead[chapter.id]}</p>
                      <p className="text-sm leading-relaxed text-foreground-800 mt-2">{chapter.prompt}</p>
                    </article>
                    <article className="ml-8 rounded-2xl rounded-tr-sm bg-amber-50 border border-amber-100 p-4">
                      <p className="text-[11px] text-amber-700">你的回應</p>
                      <p className="text-sm leading-relaxed text-foreground-700 mt-1">{answer}</p>
                    </article>
                  </div>
                );
              })}
              <article className="mr-8 rounded-2xl rounded-tl-sm border border-primary-200 bg-primary-50/60 p-4">
                <p className="text-[11px] font-medium tracking-wider text-primary-600">AI ANGEL｜第 {journey.current + 1} 站 · {result.activeChapter.id}</p>
                <p className="text-sm leading-relaxed text-foreground-700 mt-2">{mentorLead[result.activeChapter.id]}</p>
                <p className="text-sm leading-relaxed text-foreground-800 mt-3">{result.activeChapter.prompt}</p>
                <p className="text-xs leading-relaxed text-foreground-500 mt-3">不用一次回答很多。此刻你看見了什麼？</p>
              </article>
              {journey.reflections[journey.current] && (
                <>
                  <article className="ml-8 rounded-2xl rounded-tr-sm bg-amber-50 border border-amber-100 p-4">
                    <p className="text-[11px] text-amber-700">你的回應</p>
                    <p className="text-sm leading-relaxed text-foreground-700 mt-1">{journey.reflections[journey.current]}</p>
                  </article>
                  <article className="mr-8 rounded-2xl rounded-tl-sm border border-primary-100 bg-primary-50/40 p-4">
                    <p className="text-[11px] text-primary-600">AI Angel</p>
                    <p className="text-sm leading-relaxed text-foreground-700 mt-2">我收到了。先不用急著得到標準答案；你已經把原本自動運轉的經驗，帶回可以觀看與選擇的位置。若還有話，可以繼續說，或者決定一項改善的行動；準備好時，我們再走下一步。</p>
                  </article>
                </>
              )}
              <article className="ml-8 rounded-2xl rounded-tr-sm border border-amber-200 bg-background-50 p-4">
                <p className="text-[11px] font-medium tracking-wider text-amber-700">你的回應</p>
                <textarea
                  rows={3}
                  value={reflection}
                  onChange={(event) => setReflection(event.target.value)}
                  placeholder="直接和 AI Angel 說……"
                  className="mt-2 w-full rounded-lg border border-foreground-200 bg-background-50 px-3 py-3 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-300"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                  <button type="button" disabled={!reflection.trim()} onClick={() => saveReflection(false)} className="rounded-lg border border-primary-200 px-3 py-2.5 text-sm text-primary-700 disabled:opacity-40">送出，留在這一章</button>
                  {journey.current < journey.path.length - 1 ? (
                    <button type="button" disabled={!reflection.trim()} onClick={() => saveReflection(true)} className="rounded-lg bg-primary-500 px-3 py-2.5 text-sm text-white disabled:opacity-40">送出，準備走下一步</button>
                  ) : (
                    <button type="button" disabled={!reflection.trim()} onClick={() => saveReflection(false)} className="rounded-lg bg-primary-500 px-3 py-2.5 text-sm text-white disabled:opacity-40">送出並完成這次旅程</button>
                  )}
                </div>
                <p className="text-[11px] text-foreground-400 mt-3">對話與章節進度會保存在這台裝置，下次可接著談。</p>
              </article>
            </div>
          )}
          <p className="text-[11px] text-foreground-400 text-center">知識版本：{result.knowledgeVersion}｜原始回答、路由章卡與後續ABAA修正應分版保存，不互相覆蓋。</p>
        </div>
      )}
    </section>
  );
}
